import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageShell } from "@/components/Navbar";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CalendarPlus, Calendar, MapPin, Users, Loader2, Pencil, Eye } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  DRAFT: "bg-slate-100 text-slate-500", PUBLISHED: "bg-emerald-50 text-emerald-700",
  CANCELLED: "bg-red-50 text-red-600", ARCHIVED: "bg-slate-100 text-slate-400", COMPLETED: "bg-blue-50 text-blue-700",
};
const fmtDT = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

export default function MyEvents() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [rsvpFor, setRsvpFor] = useState(null);
  const [rsvps, setRsvps] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/me/events"); setItems(data); } catch { setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    try { await api.patch(`/events/${id}/status`, { action }); toast.success("Updated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const openRsvps = async (ev) => {
    setRsvpFor(ev); setRsvps(null);
    try { const { data } = await api.get(`/events/${ev.id}/rsvps`); setRsvps(data); } catch (e) { toast.error(apiError(e)); setRsvps({ going: [], interested: [], going_count: 0, interested_count: 0 }); }
  };

  return (
    <PageShell testid="my-events-page">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2"><Calendar className="h-7 w-7 text-[#C79A3B]" /> My Events</h1>
          <p className="text-sm text-slate-500 mt-1">Create and manage your real estate events.</p>
        </div>
        <Button data-testid="my-events-post-btn" onClick={() => nav("/post-event")} className="bg-[#C79A3B] text-[#0F2C59] hover:bg-[#C79A3B]/90 font-semibold rounded-xl gap-2"><CalendarPlus className="h-4 w-4" /> Post Event</Button>
      </div>

      {items === null ? <p className="text-sm text-slate-400">Loading…</p>
        : !items.length ? (
          <div data-testid="my-events-empty" className="bg-white border border-dashed border-slate-300 rounded-2xl p-14 text-center">
            <Calendar className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">No events yet</p>
            <p className="text-sm text-slate-400 mt-1">Post your first open house, launch or briefing.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((ev) => (
              <div key={ev.id} data-testid={`my-event-${ev.id}`} className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col sm:flex-row gap-4">
                <img src={ev.cover_image} alt="" className="h-28 sm:h-24 sm:w-40 w-full object-cover rounded-xl shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#0F2C59] text-white">{ev.event_type_label}</span>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLE[ev.status]}`} data-testid={`my-event-status-${ev.id}`}>{ev.status}</span>
                    {ev.is_past && <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-400">Ended</span>}
                  </div>
                  <Link to={`/event/${ev.id}`} className="block text-base font-semibold text-slate-900 mt-1.5 hover:text-[#0F2C59]">{ev.title}</Link>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-1">
                    <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {fmtDT(ev.start_at)}</span>
                    {(ev.city || ev.venue) && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {ev.city || ev.venue}</span>}
                    <button onClick={() => openRsvps(ev)} data-testid={`my-event-rsvps-${ev.id}`} className="flex items-center gap-1 hover:text-[#0F2C59]"><Users className="h-3.5 w-3.5" /> {ev.rsvp_count} RSVPs</button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <Link to={`/event/${ev.id}`} className="text-xs text-slate-600 flex items-center gap-1 hover:text-[#0F2C59]"><Eye className="h-3.5 w-3.5" /> View</Link>
                    {["DRAFT", "PUBLISHED"].includes(ev.status) && <button onClick={() => nav(`/post-event?id=${ev.id}`)} data-testid={`my-event-edit-${ev.id}`} className="text-xs text-slate-600 flex items-center gap-1 hover:text-[#0F2C59]"><Pencil className="h-3.5 w-3.5" /> Edit</button>}
                    {ev.status === "DRAFT" && <button onClick={() => act(ev.id, "publish")} data-testid={`my-event-publish-${ev.id}`} className="text-xs font-semibold text-emerald-700 hover:underline">Publish</button>}
                    {["PUBLISHED", "DRAFT"].includes(ev.status) && <button onClick={() => act(ev.id, "cancel")} data-testid={`my-event-cancel-${ev.id}`} className="text-xs text-red-600 hover:underline">Cancel</button>}
                    {ev.status !== "ARCHIVED" && <button onClick={() => act(ev.id, "archive")} data-testid={`my-event-archive-${ev.id}`} className="text-xs text-slate-400 hover:underline">Archive</button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      <Dialog open={!!rsvpFor} onOpenChange={(o) => { if (!o) { setRsvpFor(null); setRsvps(null); } }}>
        <DialogContent data-testid="rsvp-dialog">
          <DialogHeader><DialogTitle>RSVPs — {rsvpFor?.title}</DialogTitle></DialogHeader>
          {rsvps === null ? <p className="text-sm text-slate-400">Loading…</p>
            : !(rsvps.going.length + rsvps.interested.length) ? <p className="text-sm text-slate-500">No RSVPs yet.</p>
              : <div className="max-h-96 overflow-auto space-y-4">
                {[["Going", rsvps.going], ["Interested", rsvps.interested]].map(([label, list]) => list.length > 0 && (
                  <div key={label}>
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1">{label} ({list.length})</p>
                    <ul className="divide-y">
                      {list.map((a, i) => (
                        <li key={a.user_id || a.email || i} className="flex items-center gap-3 py-2.5" data-testid="rsvp-attendee-row">
                          <img src={a.avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${a.name}`} alt="" className="h-8 w-8 rounded-full object-cover" />
                          <div className="min-w-0">
                            <span className="text-sm font-medium text-slate-800 block truncate">{a.name}</span>
                            <span className="text-xs text-slate-500 block truncate">{[a.email, a.mobile].filter(Boolean).join(" · ")}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>}
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
